import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import {
  Animated,
  Image,
  FlatList,
  LayoutChangeEvent,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import {
  ChevronDown,
  Heart,
  Info,
  ListMusic,
  Monitor,
  Pause,
  Play,
  Repeat2,
  Shuffle,
  SkipBack,
  SkipForward,
  Volume2,
  Volume1,
  VolumeX,
  X,
} from "lucide-react-native";
import { AudioPlaybackSurface } from "../components/AudioPlaybackSurface";
import { AudioPlaybackHandle } from "../components/audioPlaybackTypes";
import { FocusablePressable } from "../components/FocusablePressable";
import {
  getAudioStreamUrl,
  imageSource,
  JellyfinItem,
  reportPlayerState,
  setItemFavorite,
} from "../api/jellyfin";
import { useSession } from "../session/SessionProvider";
import { colors, systemSansFont } from "../theme/tokens";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useMusicScrollIndicator } from "../components/MusicScrollIndicator";

const MUSIC_PLAYBACK_STORAGE_KEY = "jellyflix.music-playback.v1";

type MusicPlaybackValue = {
  queue: JellyfinItem[];
  currentTrack?: JellyfinItem;
  currentIndex: number;
  playing: boolean;
  loadQueue: (items: JellyfinItem[], index?: number, autoPlay?: boolean) => void;
  addToQueue: (items: JellyfinItem[]) => void;
  playShuffled: (items: JellyfinItem[]) => void;
  toggle: () => void;
  next: () => void;
  previous: () => void;
  dismiss: () => void;
};

const MusicPlaybackContext = createContext<MusicPlaybackValue | null>(null);

function formatTime(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0:00";
  const seconds = Math.floor(value);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function EqualizerBars() {
  const values = [useRef(new Animated.Value(0.45)).current, useRef(new Animated.Value(1)).current, useRef(new Animated.Value(0.6)).current];
  useEffect(() => {
    const animations = values.map((value, index) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(index * 140),
          Animated.timing(value, {
            toValue: index === 1 ? 0.35 : 1,
            duration: 380,
            useNativeDriver: true,
          }),
          Animated.timing(value, {
            toValue: index === 1 ? 1 : 0.35,
            duration: 420,
            useNativeDriver: true,
          }),
        ]),
      ),
    );
    animations.forEach((animation) => animation.start());
    return () => animations.forEach((animation) => animation.stop());
  }, []);
  return (
    <View accessibilityLabel="Now playing" style={styles.equalizer}>
      {values.map((value, index) => (
        <Animated.View
          key={index}
          style={[styles.equalizerBar, { transform: [{ scaleY: value }] }]}
        />
      ))}
    </View>
  );
}

export function MusicPlaybackProvider({ children }: PropsWithChildren) {
  const { width } = useReferenceWindowDimensions();
  const compact = width < 700;
  const { credentials } = useSession();
  const audioRef = useRef<AudioPlaybackHandle>(null);
  const progressWidth = useRef(1);
  const volumeWidth = useRef(1);
  const lastReport = useRef(0);
  const playbackStarted = useRef(false);
  const playbackHydrated = useRef(false);
  const restoredPosition = useRef(0);
  const lastPersistedSecond = useRef(0);
  const positionRef = useRef(0);
  const playingRef = useRef(false);
  const volumeRef = useRef(1);
  const currentTrackRef = useRef<JellyfinItem>();
  const credentialsRef = useRef(credentials);
  const [queue, setQueue] = useState<JellyfinItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState<"off" | "all" | "one">("off");
  const [panel, setPanel] = useState<"queue" | "details" | "info" | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [favoriteOverrides, setFavoriteOverrides] = useState<Record<string, boolean>>({});
  const [error, setError] = useState("");
  const queueScrollbar = useMusicScrollIndicator();
  const safeVolume = Number.isFinite(volume)
    ? Math.max(0, Math.min(1, volume))
    : 1;
  const applyVolume = useCallback((nextVolume: number) => {
    if (!Number.isFinite(nextVolume)) return;
    setVolume(Math.max(0, Math.min(1, nextVolume)));
  }, []);
  const currentTrack = currentIndex >= 0 ? queue[currentIndex] : undefined;
  const itemDuration = currentTrack?.RunTimeTicks
    ? currentTrack.RunTimeTicks / 10_000_000
    : 0;
  const timelineDuration = Number.isFinite(duration) && duration > 0
    ? duration
    : itemDuration;
  useEffect(() => {
    let active = true;
    void AsyncStorage.getItem(MUSIC_PLAYBACK_STORAGE_KEY).then((raw) => {
      if (!active || !raw) return;
      try {
        const saved = JSON.parse(raw) as { queue?: JellyfinItem[]; currentIndex?: number; position?: number };
        if (!saved.queue?.length) return;
        setQueue(saved.queue);
        setCurrentIndex(Math.max(0, Math.min(saved.currentIndex ?? 0, saved.queue.length - 1)));
        restoredPosition.current = Number.isFinite(saved.position) ? Math.max(0, saved.position ?? 0) : 0;
        setPosition(restoredPosition.current);
      } catch { /* Ignore stale playback snapshots. */ }
    }).finally(() => { playbackHydrated.current = true; });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (!playbackHydrated.current) return;
    if (!queue.length) {
      void AsyncStorage.removeItem(MUSIC_PLAYBACK_STORAGE_KEY);
      return;
    }
    void AsyncStorage.setItem(MUSIC_PLAYBACK_STORAGE_KEY, JSON.stringify({ queue, currentIndex, position: positionRef.current }));
  }, [currentIndex, queue]);
  positionRef.current = position;
  playingRef.current = playing;
  volumeRef.current = safeVolume;
  currentTrackRef.current = currentTrack;
  credentialsRef.current = credentials;
  const uri =
    credentials && currentTrack?.Id
      ? getAudioStreamUrl(credentials, currentTrack.Id)
      : undefined;

  const report = useCallback(
    (event: "start" | "progress" | "stop", item?: JellyfinItem) => {
      const activeCredentials = credentialsRef.current;
      const activeItem = item ?? currentTrackRef.current;
      if (!activeCredentials || !activeItem?.Id) return;
      void reportPlayerState(activeCredentials, event, {
        itemId: activeItem.Id,
        positionSeconds: positionRef.current,
        paused: !playingRef.current,
        muted: volumeRef.current === 0,
        playMethod: "DirectStream",
      }).catch(() => undefined);
    },
    [],
  );

  const selectIndex = useCallback(
    (index: number, autoPlay = playingRef.current) => {
      if (index < 0 || index >= queue.length) return;
      const activeTrack = currentTrackRef.current;
      if (playbackStarted.current && activeTrack?.Id) report("stop", activeTrack);
      playbackStarted.current = false;
      setCurrentIndex(index);
      setPosition(0);
      setDuration(0);
      setError("");
      setPlaying(autoPlay);
    },
    [queue.length, report],
  );

  const loadQueue = useCallback(
    (items: JellyfinItem[], index = 0, autoPlay = true) => {
      const playable = items.filter((item) => Boolean(item.Id));
      if (!playable.length) return;
      const activeTrack = currentTrackRef.current;
      if (playbackStarted.current && activeTrack?.Id) report("stop", activeTrack);
      playbackStarted.current = false;
      setQueue(playable);
      setCurrentIndex(Math.max(0, Math.min(index, playable.length - 1)));
      setPosition(0);
      setDuration(0);
      setError("");
      setPlaying(autoPlay);
    },
    [report],
  );

  const next = useCallback(() => {
    if (!queue.length) return;
    selectIndex(currentIndex < queue.length - 1 ? currentIndex + 1 : 0, true);
  }, [currentIndex, queue.length, selectIndex]);
  const previous = useCallback(() => {
    if (position > 4) {
      audioRef.current?.seek(0);
      setPosition(0);
      return;
    }
    if (!queue.length) return;
    selectIndex(currentIndex > 0 ? currentIndex - 1 : queue.length - 1, true);
  }, [currentIndex, position, queue.length, selectIndex]);
  const addToQueue = useCallback((items: JellyfinItem[]) => {
    setQueue((existing) => [
      ...existing,
      ...items.filter(
        (item) => item.Id && !existing.some((entry) => entry.Id === item.Id),
      ),
    ]);
  }, []);
  const playShuffled = useCallback(
    (items: JellyfinItem[]) => {
      const shuffled = [...items];
      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const swap = Math.floor(Math.random() * (index + 1));
        [shuffled[index], shuffled[swap]] = [shuffled[swap], shuffled[index]];
      }
      setShuffle(true);
      loadQueue(shuffled, 0, true);
    },
    [loadQueue],
  );
  const toggle = useCallback(() => {
    if (!currentTrack) return;
    setPlaying((value) => !value);
  }, [currentTrack]);
  const handleEnded = useCallback(() => {
    if (repeat === "one") {
      audioRef.current?.seek(0);
      setPosition(0);
      setPlaying(false);
      setTimeout(() => setPlaying(true), 0);
      return;
    }
    if (currentIndex < queue.length - 1) selectIndex(currentIndex + 1, true);
    else if (repeat === "all" && queue.length) selectIndex(0, true);
    else setPlaying(false);
  }, [currentIndex, queue.length, repeat, selectIndex]);
  const dismiss = useCallback(() => {
    const activeTrack = currentTrackRef.current;
    if (playbackStarted.current && activeTrack?.Id) report("stop", activeTrack);
    playbackStarted.current = false;
    setPlaying(false);
    setQueue([]);
    setCurrentIndex(-1);
    setPosition(0);
    setDuration(0);
    setError("");
    setMobileExpanded(false);
  }, [report]);
  const contextValue = useMemo<MusicPlaybackValue>(
    () => ({
      queue,
      currentTrack,
      currentIndex,
      playing,
      loadQueue,
      addToQueue,
      playShuffled,
      toggle,
      next,
      previous,
      dismiss,
    }),
    [
      addToQueue,
      currentIndex,
      currentTrack,
      dismiss,
      loadQueue,
      next,
      playShuffled,
      playing,
      previous,
      queue,
      toggle,
    ],
  );
  const playedFlex = Math.max(0.001, Math.min(position, Math.max(timelineDuration, 1)));
  const remainingFlex = Math.max(0.001, Math.max(timelineDuration, 1) - playedFlex);
  const seekFromEvent = (locationX: number) => {
    const nextPosition = Math.max(
      0,
      Math.min(timelineDuration, (locationX / progressWidth.current) * timelineDuration),
    );
    audioRef.current?.seek(nextPosition);
    setPosition(nextPosition);
  };
  const seekToRatio = (ratio: number) => seekFromEvent(Math.max(0, Math.min(1, ratio)) * progressWidth.current);

  return (
    <MusicPlaybackContext.Provider value={contextValue}>
      <View
        style={[
          styles.app,
          currentTrack && (compact ? styles.appWithMobilePlayer : styles.appWithPlayer),
        ]}
      >
        {children}
      </View>
      <AudioPlaybackSurface
        ref={audioRef}
        uri={uri}
        headers={
          credentials ? { "X-Emby-Token": credentials.accessToken } : undefined
        }
        paused={!playing}
        volume={safeVolume}
        onReady={(nextDuration) => {
          const resolvedDuration = Number.isFinite(nextDuration) && nextDuration > 0
            ? nextDuration
            : itemDuration;
          if (resolvedDuration > 0) setDuration(resolvedDuration);
          if (restoredPosition.current > 0) {
            audioRef.current?.seek(Math.min(restoredPosition.current, resolvedDuration || restoredPosition.current));
            restoredPosition.current = 0;
          }
          playbackStarted.current = true;
          report("start");
        }}
        onProgress={(nextPosition) => {
          setPosition(nextPosition);
          const persistedSecond = Math.floor(nextPosition / 5) * 5;
          if (persistedSecond > lastPersistedSecond.current && queue.length) {
            lastPersistedSecond.current = persistedSecond;
            void AsyncStorage.setItem(MUSIC_PLAYBACK_STORAGE_KEY, JSON.stringify({ queue, currentIndex, position: nextPosition }));
          }
          if (nextPosition - lastReport.current >= 10) {
            lastReport.current = nextPosition;
            report("progress");
          }
        }}
        onEnded={handleEnded}
        onError={setError}
      />
      {currentTrack && credentials ? (compact ? (mobileExpanded ? (
        <View style={styles.mobileExpanded}>
          <View style={styles.mobileExpandedHeader}>
            <MusicButton label="Collapse player" onPress={() => setMobileExpanded(false)}>
              <ChevronDown color={colors.text} size={24} />
            </MusicButton>
            <Text style={styles.mobileNowPlaying}>Now Playing</Text>
            <MusicButton label="Close player" onPress={dismiss}>
              <X color={colors.textMuted} size={20} />
            </MusicButton>
          </View>
          <View style={styles.mobileExpandedArtWrap}>
            <Image
              source={imageSource(credentials, currentTrack, "Primary", 700)}
              resizeMode="cover"
              style={[
                styles.mobileExpandedArt,
                {
                  width: Math.min(width - 48, 400),
                  height: Math.min(width - 48, 400),
                },
              ]}
            />
          </View>
          <View style={styles.mobileExpandedBottom}>
            <View style={styles.mobileExpandedCopy}>
              <View style={styles.trackCopy}>
                <Text numberOfLines={1} style={styles.mobileExpandedTitle}>
                  {currentTrack.Name ?? "Untitled"}
                </Text>
                <Text numberOfLines={1} style={styles.mobileExpandedArtist}>
                  {currentTrack.ArtistItems?.map((artist) => artist.Name)
                    .filter(Boolean)
                    .join(", ") || currentTrack.AlbumArtist || "Unknown artist"}
                </Text>
              </View>
              <MusicButton
                label={(favoriteOverrides[currentTrack.Id ?? ""] ?? currentTrack.UserData?.IsFavorite) ? "Remove from favorites" : "Add to favorites"}
                onPress={() => {
                  if (!currentTrack.Id) return;
                  const current = favoriteOverrides[currentTrack.Id] ?? Boolean(currentTrack.UserData?.IsFavorite);
                  const nextFavorite = !current;
                  setFavoriteOverrides((values) => ({ ...values, [currentTrack.Id!]: nextFavorite }));
                  void setItemFavorite(credentials, currentTrack.Id, nextFavorite).catch(() =>
                    setFavoriteOverrides((values) => ({ ...values, [currentTrack.Id!]: current })),
                  );
                }}
              >
                <Heart color={(favoriteOverrides[currentTrack.Id ?? ""] ?? currentTrack.UserData?.IsFavorite) ? colors.brand : colors.textMuted} fill={(favoriteOverrides[currentTrack.Id ?? ""] ?? currentTrack.UserData?.IsFavorite) ? colors.brand : "transparent"} size={20} />
              </MusicButton>
            </View>
            <View style={styles.mobileExpandedTimeline}>
              <PlayerSlider
                label="Music playback position"
                playedFlex={playedFlex}
                remainingFlex={remainingFlex}
                onLayout={(event) => {
                  progressWidth.current = Math.max(1, event.nativeEvent.layout.width);
                }}
                onRatioChange={seekToRatio}
                style={styles.mobileTimeline}
              />
              <View style={styles.mobileTimeRow}>
                <Text style={styles.mobileTime}>{formatTime(position)}</Text>
                <Text style={styles.mobileTime}>{formatTime(timelineDuration)}</Text>
              </View>
            </View>
            <View style={styles.mobileExpandedControls}>
              <MusicButton label="Shuffle" onPress={() => setShuffle((value) => !value)}>
                <Shuffle color={shuffle ? colors.brand : colors.textMuted} size={20} />
                {shuffle ? <View style={styles.activeDot} /> : null}
              </MusicButton>
              <MusicButton label="Previous" onPress={previous}>
                <SkipBack color={colors.text} size={28} />
              </MusicButton>
              <MusicButton label={playing ? "Pause" : "Play"} primary onPress={toggle}>
                {playing ? <Pause color="#111" fill="#111" size={26} /> : <Play color="#111" fill="#111" size={26} />}
              </MusicButton>
              <MusicButton label="Next" onPress={next}>
                <SkipForward color={colors.text} size={28} />
              </MusicButton>
              <MusicButton label={`Repeat: ${repeat}`} onPress={() => setRepeat((value) => value === "off" ? "all" : value === "all" ? "one" : "off")}>
                <Repeat2 color={repeat === "off" ? colors.textMuted : colors.brand} size={20} />
                {repeat !== "off" ? <View style={styles.activeDot} /> : null}
                {repeat === "one" ? <Text style={styles.repeatOne}>1</Text> : null}
              </MusicButton>
            </View>
            <View style={styles.mobileVolumeRow}>
              <VolumeX color={colors.textMuted} size={16} />
              <PlayerSlider
                label="Music volume"
                playedFlex={Math.max(0.001, safeVolume)}
                remainingFlex={Math.max(0.001, 1 - safeVolume)}
                onLayout={(event) => {
                  volumeWidth.current = Math.max(1, event.nativeEvent.layout.width);
                }}
                onRatioChange={applyVolume}
                style={styles.mobileVolumeSlider}
              />
              <Volume2 color={colors.textMuted} size={16} />
            </View>
          </View>
          {error ? <Text numberOfLines={1} style={styles.error}>{error}</Text> : null}
        </View>
      ) : (
        <View style={styles.mobileBar}>
          <View style={styles.mobileProgressRow}>
            <View style={[styles.mobileProgressPlayed, { flex: playedFlex }]} />
            <View style={[styles.mobileProgressRemaining, { flex: remainingFlex }]} />
          </View>
          <View style={styles.mobileBarInner}>
            <Pressable onPress={() => setMobileExpanded(true)} style={styles.mobileSummary}>
              <Image source={imageSource(credentials, currentTrack, "Primary", 80)} resizeMode="cover" style={styles.mobileArt} />
              <View style={styles.trackCopy}>
                <Text numberOfLines={1} style={styles.title}>{currentTrack.Name ?? "Untitled"}</Text>
                <Text numberOfLines={1} style={styles.artist}>{currentTrack.AlbumArtist ?? "Unknown artist"}</Text>
              </View>
            </Pressable>
            <MusicButton label={playing ? "Pause" : "Play"} onPress={toggle}>
              {playing ? <Pause color={colors.text} fill={colors.text} size={21} /> : <Play color={colors.text} fill={colors.text} size={21} />}
            </MusicButton>
            <MusicButton label="Next" onPress={next}>
              <SkipForward color={colors.text} size={22} />
            </MusicButton>
            <MusicButton label="Close player" onPress={dismiss}>
              <X color={colors.textMuted} size={20} />
            </MusicButton>
          </View>
          {error ? <Text numberOfLines={1} style={styles.error}>{error}</Text> : null}
        </View>
      )) : (
        <>
          {panel ? (
            <View style={styles.sidePanel}>
              <View style={styles.sidePanelHeader}>
                <View style={styles.sidePanelTabs}>
                  {([['details', 'Now Playing'], ['info', 'Info'], ['queue', 'Queue']] as const).map(([value, label]) => (
                    <FocusablePressable key={value} accessibilityLabel={label} onPress={() => setPanel(value)} style={() => [styles.sidePanelTab, panel === value && styles.sidePanelTabActive]}>
                      <Text style={[styles.sidePanelTabText, panel === value && styles.sidePanelTabTextActive]}>{label}</Text>
                    </FocusablePressable>
                  ))}
                </View>
                <MusicButton label="Close panel" onPress={() => setPanel(null)}><X color={colors.textMuted} size={18} /></MusicButton>
              </View>
              {panel === "queue" ? (
                <View style={styles.queueListWrap}>
                <FlatList
                  {...queueScrollbar.listProps}
                  contentContainerStyle={styles.queueContent}
                  data={queue.slice(Math.max(0, currentIndex))}
                  keyExtractor={(item, index) => item.Id ?? `queue-${index}`}
                  renderItem={({ item, index }) => (
                    <View>
                    {index === 1 ? <Text style={[styles.queueSectionLabel, styles.queueNextLabel]}>NEXT IN QUEUE</Text> : null}
                    <FocusablePressable
                      accessibilityLabel={`Play ${item.Name ?? `track ${index + 1}`}`}
                      onPress={() => selectIndex(currentIndex + index, true)}
                      style={(focused, pressed, hovered) => [styles.queueItem, index === 0 && styles.queueItemCurrent, (focused || hovered) && styles.queueItemActive, pressed && styles.pressed]}
                    >
                      <Image source={imageSource(credentials, item, "Primary", 80)} resizeMode="cover" style={styles.queueItemArt} />
                      <View style={styles.queueItemCopy}>
                        <Text numberOfLines={1} style={[styles.queueItemTitle, index === 0 && styles.queueItemTitleCurrent]}>{item.Name ?? "Untitled"}</Text>
                        <Text numberOfLines={1} style={styles.queueItemArtist}>{item.AlbumArtist ?? "Unknown artist"}</Text>
                      </View>
                      {index === 0 && playing ? <EqualizerBars /> : null}
                    </FocusablePressable>
                    </View>
                  )}
                  ListHeaderComponent={<Text style={styles.queueSectionLabel}>NOW PLAYING</Text>}
                />
                {queueScrollbar.indicator}
                </View>
              ) : panel === "info" ? (
                <View style={styles.infoPanel}>
                  <Image source={imageSource(credentials, currentTrack, "Primary", 500)} resizeMode="cover" style={styles.infoArt} />
                  <Text numberOfLines={2} style={styles.infoTitle}>{currentTrack.Name ?? "Untitled"}</Text>
                  <Text style={styles.infoArtist}>{currentTrack.AlbumArtist ?? "Unknown artist"}</Text>
                  <Text style={styles.infoAlbum}>{currentTrack.Album ?? ""}</Text>
                  {playing ? <EqualizerBars /> : null}
                </View>
              ) : (
                <View style={styles.detailsPanel}>
                  <Image source={imageSource(credentials, currentTrack, "Primary", 500)} resizeMode="cover" style={styles.detailsArt} />
                  <Text numberOfLines={2} style={styles.detailsTitle}>{currentTrack.Name ?? "Untitled"}</Text>
                  <Text style={styles.detailsArtist}>{currentTrack.AlbumArtist ?? "Unknown artist"}</Text>
                  <Text style={styles.detailsAlbum}>{currentTrack.Album ?? ""}</Text>
                  <Heart color={(favoriteOverrides[currentTrack.Id ?? ""] ?? currentTrack.UserData?.IsFavorite) ? colors.brand : colors.textMuted} fill={(favoriteOverrides[currentTrack.Id ?? ""] ?? currentTrack.UserData?.IsFavorite) ? colors.brand : "transparent"} size={18} style={styles.detailsHeart} />
                  {queue[currentIndex + 1] ? <View style={styles.nextBlock}><Text style={styles.queueSectionLabel}>NEXT IN QUEUE</Text><View style={styles.nextRow}><Image source={imageSource(credentials, queue[currentIndex + 1], "Primary", 80)} style={styles.queueItemArt} /><View><Text numberOfLines={1} style={styles.queueItemTitle}>{queue[currentIndex + 1].Name}</Text><Text style={styles.queueItemArtist}>{queue[currentIndex + 1].AlbumArtist}</Text></View></View></View> : null}
                </View>
              )}
            </View>
          ) : null}
          <View style={styles.bar}>
          <View style={[styles.track, compact && styles.trackCompact]}>
            <Image
              source={imageSource(credentials, currentTrack, "Primary", 96)}
              resizeMode="cover"
              style={styles.art}
            />
            <View style={[styles.trackCopy, styles.desktopTrackCopy]}>
              <View style={styles.titleRow}>
                {playing ? <EqualizerBars /> : null}
                <Text numberOfLines={1} style={styles.title}>
                  {currentTrack.Name ?? "Untitled"}
                </Text>
              </View>
              <Text numberOfLines={1} style={styles.artist}>
                {currentTrack.ArtistItems?.map((artist) => artist.Name)
                  .filter(Boolean)
                  .join(", ") ||
                  currentTrack.AlbumArtist ||
                  "Unknown artist"}
              </Text>
            </View>
            <View style={styles.desktopHeart}>
            <MusicButton
              label={(favoriteOverrides[currentTrack.Id ?? ""] ?? currentTrack.UserData?.IsFavorite) ? "Remove from favorites" : "Add to favorites"}
              onPress={() => {
                if (!currentTrack.Id) return;
                const current = favoriteOverrides[currentTrack.Id] ?? Boolean(currentTrack.UserData?.IsFavorite);
                const nextFavorite = !current;
                setFavoriteOverrides((values) => ({ ...values, [currentTrack.Id!]: nextFavorite }));
                void setItemFavorite(credentials, currentTrack.Id, nextFavorite).catch(() =>
                  setFavoriteOverrides((values) => ({ ...values, [currentTrack.Id!]: current })),
                );
              }}
            >
              <Heart color={(favoriteOverrides[currentTrack.Id ?? ""] ?? currentTrack.UserData?.IsFavorite) ? colors.brand : colors.textMuted} fill={(favoriteOverrides[currentTrack.Id ?? ""] ?? currentTrack.UserData?.IsFavorite) ? colors.brand : "transparent"} size={16} />
            </MusicButton>
            </View>
          </View>
          <View style={styles.transport}>
            <View style={styles.buttons}>
              <MusicButton label="Shuffle" onPress={() => setShuffle((value) => !value)}>
                <Shuffle color={shuffle ? colors.brand : colors.textMuted} size={16} />
                {shuffle ? <View style={styles.activeDot} /> : null}
              </MusicButton>
              <MusicButton label="Previous" onPress={previous}>
                <SkipBack color={colors.text} size={18} />
              </MusicButton>
              <MusicButton label={playing ? "Pause" : "Play"} primary onPress={toggle}>
                {playing ? (
                  <Pause color="#111" fill="#111" size={18} />
                ) : (
                  <Play color="#111" fill="#111" size={18} />
                )}
              </MusicButton>
              <MusicButton label="Next" onPress={next}>
                <SkipForward color={colors.text} size={18} />
              </MusicButton>
              <MusicButton label={`Repeat: ${repeat}`} onPress={() => setRepeat((value) => value === "off" ? "all" : value === "all" ? "one" : "off")}>
                <Repeat2 color={repeat === "off" ? colors.textMuted : colors.brand} size={16} />
                {repeat !== "off" ? <View style={styles.activeDot} /> : null}
                {repeat === "one" ? <Text style={styles.repeatOne}>1</Text> : null}
              </MusicButton>
            </View>
            <View style={styles.timelineRow}>
              <Text style={styles.time}>{formatTime(position)}</Text>
              <PlayerSlider
                label="Music playback position"
                playedFlex={playedFlex}
                remainingFlex={remainingFlex}
                onLayout={(event: LayoutChangeEvent) => {
                  progressWidth.current = Math.max(1, event.nativeEvent.layout.width);
                }}
                onRatioChange={seekToRatio}
                style={styles.timeline}
              />
              <Text style={styles.time}>{formatTime(timelineDuration)}</Text>
            </View>
          </View>
          <View style={styles.volumeBlock}>
            <MusicButton label="Now Playing" onPress={() => setPanel((value) => value === "details" ? null : "details")}>
              <Monitor color={panel === "details" ? colors.brand : colors.textMuted} size={18} />
            </MusicButton>
            <MusicButton label="Queue" onPress={() => setPanel((value) => value === "queue" ? null : "queue")}>
              <ListMusic color={panel === "queue" ? colors.brand : colors.textMuted} size={18} />
            </MusicButton>
            <MusicButton label={safeVolume === 0 ? "Unmute" : "Mute"} onPress={() => applyVolume(safeVolume === 0 ? 0.5 : 0)}>
              {safeVolume === 0 ? <VolumeX color={colors.textMuted} size={18} /> : safeVolume < 0.5 ? <Volume1 color={colors.textMuted} size={18} /> : <Volume2 color={colors.textMuted} size={18} />}
            </MusicButton>
            <PlayerSlider
              label="Music volume"
              playedFlex={Math.max(0.001, safeVolume)}
              remainingFlex={Math.max(0.001, 1 - safeVolume)}
              onLayout={(event) => {
                volumeWidth.current = Math.max(1, event.nativeEvent.layout.width);
              }}
              onRatioChange={applyVolume}
              style={styles.volumeSlider}
            />
            <MusicButton label="Close player" onPress={dismiss}>
              <X color={colors.textMuted} size={18} />
            </MusicButton>
          </View>
          {error ? <Text numberOfLines={1} style={styles.error}>{error}</Text> : null}
        </View>
        </>
      )) : null}
    </MusicPlaybackContext.Provider>
  );
}

type PlayerSliderProps = {
  label: string;
  playedFlex: number;
  remainingFlex: number;
  onLayout?: (event: LayoutChangeEvent) => void;
  onRatioChange: (ratio: number) => void;
  style: object;
};

function PlayerSlider({
  label,
  playedFlex,
  remainingFlex,
  onLayout,
  onRatioChange,
  style,
}: PlayerSliderProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const sliderRef = useRef<React.ElementRef<typeof Pressable>>(null);
  const sliderWidth = useRef(1);
  const active = hovered || focused;
  return (
    <Pressable
      ref={sliderRef}
      accessibilityRole="adjustable"
      accessibilityLabel={label}
      focusable
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onLayout={(event) => {
        sliderWidth.current = Math.max(1, event.nativeEvent.layout.width);
        onLayout?.(event);
      }}
      onPress={(event) => {
        const localX = Number(event.nativeEvent.locationX);
        if (Number.isFinite(localX)) {
          onRatioChange(Math.max(0, Math.min(1, localX / sliderWidth.current)));
          return;
        }
        const pointerX = Number(event.nativeEvent.pageX);
        sliderRef.current?.measure((_x, _y, measuredWidth, _height, pageX) => {
          if (!Number.isFinite(pointerX) || !Number.isFinite(pageX)) return;
          onRatioChange(Math.max(0, Math.min(1, (pointerX - pageX) / Math.max(1, measuredWidth))));
        });
      }}
      style={style}
    >
      <View
        style={[
          styles.sliderPlayed,
          active && styles.sliderPlayedActive,
          { flex: playedFlex },
        ]}
      />
      <View style={[styles.sliderThumb, active && styles.sliderThumbActive]} />
      <View style={[styles.sliderRemaining, { flex: remainingFlex }]} />
    </Pressable>
  );
}

function MusicButton({
  label,
  onPress,
  primary,
  children,
}: PropsWithChildren<{ label: string; onPress: () => void; primary?: boolean }>) {
  return (
    <FocusablePressable
      accessibilityLabel={label}
      onPress={onPress}
      style={(focused, pressed, hovered) => [
        styles.control,
        primary && styles.controlPrimary,
        (focused || hovered) && styles.controlActive,
        primary && (focused || hovered) && styles.controlPrimaryActive,
        pressed && styles.pressed,
      ]}
    >
      {children}
    </FocusablePressable>
  );
}

export function useMusicPlayback() {
  const value = useContext(MusicPlaybackContext);
  if (!value) throw new Error("useMusicPlayback must be used inside MusicPlaybackProvider.");
  return value;
}

const styles = StyleSheet.create({
  app: { flex: 1 },
  appWithPlayer: { paddingBottom: 90 },
  appWithMobilePlayer: { paddingBottom: 74 },
  bar: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 90,
    zIndex: 100,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: "rgba(255,255,255,0.08)",
    gap: 16,
    backgroundColor: "#181818",
  },
  track: { flex: 1, minWidth: 180, flexDirection: "row", alignItems: "center", gap: 12 },
  trackCompact: { width: 132 },
  art: { width: 56, height: 56, borderRadius: 4, backgroundColor: "#242424", shadowColor: "#000", shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.5, shadowRadius: 8, elevation: 8 },
  trackCopy: { flex: 1, minWidth: 0, gap: 2 },
  desktopTrackCopy: { flexGrow: 0, flexShrink: 1, flexBasis: "auto", maxWidth: 180 },
  desktopHeart: { marginLeft: 4, flexShrink: 0 },
  titleRow: { minWidth: 0, flexDirection: "row", alignItems: "center", gap: 6 },
  title: { flexShrink: 1, color: colors.text, fontFamily: systemSansFont, fontSize: 14, lineHeight: 17, fontWeight: "600" },
  equalizer: { height: 12, flexDirection: "row", alignItems: "flex-end", gap: 2 },
  equalizerBar: { width: 3, height: 12, borderRadius: 1, backgroundColor: colors.brand },
  artist: { color: "#b3b3b3", fontFamily: systemSansFont, fontSize: 12, lineHeight: 14, fontWeight: "400" },
  transport: { flex: 2, alignItems: "center", gap: 6 },
  buttons: { flexDirection: "row", alignItems: "center", gap: 16 },
  control: { width: 32, height: 32, alignItems: "center", justifyContent: "center", borderRadius: 4 },
  controlPrimary: { width: 32, height: 32, borderRadius: 16, backgroundColor: colors.text },
  controlActive: { transform: [{ scale: 1.05 }] },
  controlPrimaryActive: { transform: [{ scale: 1.1 }] },
  activeDot: { position: "absolute", bottom: -1, width: 4, height: 4, borderRadius: 2, backgroundColor: colors.brand },
  repeatOne: { position: "absolute", top: -3, right: -2, overflow: "hidden", paddingHorizontal: 3, paddingVertical: 1, borderRadius: 3, color: colors.text, backgroundColor: colors.brand, fontFamily: systemSansFont, fontSize: 9, lineHeight: 9, fontWeight: "800" },
  pressed: { opacity: 0.78 },
  timelineRow: { alignSelf: "stretch", flexDirection: "row", alignItems: "center", gap: 8 },
  timeline: { flex: 1, height: 12, flexDirection: "row", alignItems: "center" },
  sliderPlayed: { height: 4, borderRadius: 2, backgroundColor: colors.text },
  sliderPlayedActive: { backgroundColor: colors.brand },
  sliderRemaining: { height: 4, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.2)" },
  sliderThumb: { width: 0, height: 0, borderRadius: 6, backgroundColor: colors.text },
  sliderThumbActive: { width: 12, height: 12, marginHorizontal: -1 },
  time: { width: 34, color: "#b3b3b3", fontFamily: systemSansFont, fontSize: 11, fontWeight: "400", textAlign: "center" },
  volumeBlock: { flex: 1, minWidth: 180, flexDirection: "row", justifyContent: "flex-end", alignItems: "center", gap: 5 },
  volumeSlider: { width: 128, height: 14, flexDirection: "row", alignItems: "center" },
  mobileBar: { position: "absolute", left: 8, right: 8, bottom: 8, zIndex: 100, minHeight: 58, overflow: "hidden", borderRadius: 8, backgroundColor: "#282828" },
  mobileProgressRow: { height: 2, flexDirection: "row" },
  mobileProgressPlayed: { height: 2, backgroundColor: colors.brand },
  mobileProgressRemaining: { height: 2, backgroundColor: "rgba(255,255,255,0.15)" },
  mobileBarInner: { minHeight: 56, flexDirection: "row", alignItems: "center", gap: 8, paddingHorizontal: 10, paddingVertical: 8 },
  mobileSummary: { flex: 1, minWidth: 0, flexDirection: "row", alignItems: "center", gap: 8 },
  mobileArt: { width: 40, height: 40, borderRadius: 4, backgroundColor: "#202020" },
  mobileExpanded: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 200,
    paddingTop: 10,
    backgroundColor: "#121212",
  },
  mobileExpandedHeader: {
    height: 52,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  mobileNowPlaying: { color: colors.text, fontFamily: systemSansFont, fontSize: 13, lineHeight: 17, fontWeight: "800", textTransform: "uppercase", letterSpacing: 0.7 },
  mobileExpandedArtWrap: { flex: 1, minHeight: 0, alignItems: "center", justifyContent: "center", paddingHorizontal: 24, paddingVertical: 12 },
  mobileExpandedArt: { maxWidth: 400, maxHeight: 400, borderRadius: 6, backgroundColor: "#242424", shadowColor: "#000", shadowOpacity: 0.5, shadowRadius: 22, elevation: 10 },
  mobileExpandedBottom: { paddingHorizontal: 24, paddingBottom: 24 },
  mobileExpandedCopy: { minHeight: 58, flexDirection: "row", alignItems: "center", gap: 12 },
  mobileExpandedTitle: { color: colors.text, fontFamily: systemSansFont, fontSize: 21, lineHeight: 26, fontWeight: "800" },
  mobileExpandedArtist: { marginTop: 3, color: colors.textMuted, fontFamily: systemSansFont, fontSize: 14, lineHeight: 18, fontWeight: "500" },
  mobileExpandedTimeline: { marginTop: 12 },
  mobileTimeline: { height: 18, flexDirection: "row", alignItems: "center" },
  mobileTimeRow: { marginTop: 1, flexDirection: "row", justifyContent: "space-between" },
  mobileTime: { color: colors.textMuted, fontFamily: systemSansFont, fontSize: 11, lineHeight: 14, fontWeight: "400" },
  mobileExpandedControls: { minHeight: 72, marginTop: 10, flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  mobileVolumeRow: { height: 34, marginTop: 4, flexDirection: "row", alignItems: "center", gap: 10 },
  mobileVolumeSlider: { flex: 1, height: 18, flexDirection: "row", alignItems: "center" },
  sidePanel: { position: "absolute", right: 0, top: 72, bottom: 90, width: 340, zIndex: 10, overflow: "hidden", borderLeftWidth: 1, borderLeftColor: "rgba(255,255,255,0.08)", backgroundColor: "#121212" },
  sidePanelHeader: { height: 48, paddingHorizontal: 12, flexDirection: "row", alignItems: "center", borderBottomWidth: 1, borderBottomColor: "rgba(255,255,255,0.08)" },
  sidePanelTabs: { flex: 1, height: 48, flexDirection: "row", alignItems: "stretch" },
  sidePanelTab: { paddingHorizontal: 12, justifyContent: "center", borderBottomWidth: 2, borderBottomColor: "transparent" },
  sidePanelTabActive: { borderBottomColor: colors.brand },
  sidePanelTabText: { color: colors.textMuted, fontFamily: systemSansFont, fontSize: 14, fontWeight: "700" },
  sidePanelTabTextActive: { color: colors.text },
  queueContent: { padding: 16 },
  queueListWrap: { flex: 1 },
  queueSectionLabel: { marginBottom: 10, color: colors.textMuted, fontFamily: systemSansFont, fontSize: 12, lineHeight: 16, fontWeight: "700", letterSpacing: 0.7 },
  queueNextLabel: { marginTop: 18 },
  queueItem: { minHeight: 52, paddingVertical: 6, paddingRight: 6, paddingLeft: 8, flexDirection: "row", alignItems: "center", gap: 12, borderLeftWidth: 3, borderLeftColor: "transparent", borderRadius: 3 },
  queueItemCurrent: { borderLeftColor: colors.brand },
  queueItemActive: { backgroundColor: "rgba(255,255,255,0.08)" },
  queueItemArt: { width: 40, height: 40, borderRadius: 3, backgroundColor: "#282828" },
  queueItemCopy: { flex: 1, minWidth: 0 },
  queueItemTitle: { color: colors.text, fontFamily: systemSansFont, fontSize: 14, fontWeight: "500" },
  queueItemTitleCurrent: { color: colors.brand },
  queueItemArtist: { marginTop: 3, color: colors.textMuted, fontFamily: systemSansFont, fontSize: 12, fontWeight: "400" },
  detailsPanel: { flex: 1, alignItems: "center", padding: 16 },
  detailsArt: { width: 220, height: 220, borderRadius: 4, backgroundColor: "#242424", shadowColor: "#000", shadowOpacity: 0.45, shadowRadius: 24, elevation: 12 },
  detailsTitle: { marginTop: 16, color: colors.text, fontFamily: systemSansFont, fontSize: 17, lineHeight: 22, fontWeight: "700", textAlign: "center" },
  detailsArtist: { marginTop: 5, color: colors.textMuted, fontFamily: systemSansFont, fontSize: 14 },
  detailsAlbum: { marginTop: 3, color: "rgba(255,255,255,0.4)", fontFamily: systemSansFont, fontSize: 12 },
  detailsHeart: { marginTop: 14 },
  nextBlock: { alignSelf: "stretch", marginTop: 22, paddingTop: 14, borderTopWidth: 1, borderTopColor: "rgba(255,255,255,0.08)" },
  nextRow: { flexDirection: "row", alignItems: "center", gap: 12 },
  infoPanel: { flex: 1, alignItems: "center", padding: 16 },
  infoArt: { width: 200, height: 200, borderRadius: 4, backgroundColor: "#242424", shadowColor: "#000", shadowOpacity: 0.4, shadowRadius: 20, elevation: 10 },
  infoTitle: { marginTop: 16, color: colors.text, fontFamily: systemSansFont, fontSize: 18, lineHeight: 23, fontWeight: "700", textAlign: "center" },
  infoArtist: { marginTop: 5, color: colors.textMuted, fontFamily: systemSansFont, fontSize: 14 },
  infoAlbum: { marginTop: 3, marginBottom: 12, color: "rgba(255,255,255,0.4)", fontFamily: systemSansFont, fontSize: 12 },
  error: { position: "absolute", left: 12, right: 12, bottom: 2, color: "#ff7b83", fontFamily: systemSansFont, fontSize: 10, textAlign: "center" },
});
